import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { timeout, catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { environment } from '../../../environment/environment';
import { EventDTO } from '../../../dto/event/event.dto';

@Component({
  selector: 'app-duna-event-ics',
  standalone: true,
  imports: [MatProgressSpinnerModule, MatButtonModule],
  templateUrl: './duna-event-ics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-event-ics.component.scss'],
})
export class DunaEventIcsComponent implements OnInit, OnDestroy {
  event: EventDTO | null = null;
  loading = true;
  error: string | null = null;
  private eventUuid: string | null = null;
  private loadingGuardTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.eventUuid =
        params['eventUuid'] ?? this.getEventUuidFromUrl() ?? null;
      if (this.eventUuid) {
        this.loadEvent();
      } else {
        this.loading = false;
        this.error = 'Ugyldig adresse – ingen event-UUID i URL.';
        this.cdr.markForCheck();
      }
    });
  }

  /** When route has no eventUuid param (e.g. under catch-all), read UUID from browser URL path (/ics/:eventUuid). */
  private getEventUuidFromUrl(): string | null {
    const path =
      typeof window !== 'undefined'
        ? window.location.pathname
        : this.router.url;
    const segments = path.replace(/^\//, '').split('/');
    const i = segments.indexOf('ics');
    if (i !== -1 && segments[i + 1]) {
      const candidate = segments[i + 1];
      if (candidate === 'online') return null;
      return UUID_REGEX.test(candidate) ? candidate : null;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.clearLoadingGuard();
  }

  private clearLoadingGuard(): void {
    if (this.loadingGuardTimer != null) {
      clearTimeout(this.loadingGuardTimer);
      this.loadingGuardTimer = null;
    }
  }

  private loadEvent(): void {
    if (!this.eventUuid) return;
    this.clearLoadingGuard();
    this.loading = true;
    this.error = null;
    this.event = null;
    this.loadingGuardTimer = setTimeout(() => {
      this.loadingGuardTimer = null;
      if (this.loading) {
        this.ngZone.run(() => {
          this.loading = false;
          this.error = 'Forespørgslen tog for lang tid. Prøv igen senere.';
          this.cdr.markForCheck();
        });
      }
    }, 20000);
    let base = (environment.apiUrl || '').replace(/\/$/, '');
    if (base && !/^https?:\/\//i.test(base)) {
      base = 'https://' + base;
    }
    const url = `${base}/events/${this.eventUuid}`;
    this.http
      .get<EventDTO>(url)
      .pipe(
        timeout(15000),
        catchError((err) => of({ _error: err }))
      )
      .subscribe({
        next: (result: EventDTO | { _error: any }) => {
          this.clearLoadingGuard();
          const err = (result as { _error?: any })?._error;
          if (err) {
            this.ngZone.run(() => {
              this.loading = false;
              this.error =
                err.name === 'TimeoutError'
                  ? 'Forespørgslen tog for lang tid. Prøv igen senere.'
                  : err.status === 404
                  ? 'Eventet blev ikke fundet.'
                  : err.status === 0 ||
                    (err.message &&
                      /CORS|Network|Failed to fetch/i.test(err.message))
                  ? 'Eventet kunne ikke hentes (netværk/CORS). Prøv igen senere.'
                  : 'Kunne ikke hente event. Prøv igen senere.';
              this.cdr.markForCheck();
            });
          } else {
            this.ngZone.run(() => {
              this.event = result as EventDTO;
              this.loading = false;
              this.cdr.markForCheck();
            });
          }
        },
        error: (err: any) => {
          this.clearLoadingGuard();
          this.ngZone.run(() => {
            this.loading = false;
            this.error =
              err?.status === 404
                ? 'Eventet blev ikke fundet.'
                : err?.status === 0 ||
                  (err?.message &&
                    /CORS|Network|Failed to fetch/i.test(err.message))
                ? 'Eventet kunne ikke hentes (netværk/CORS). Prøv igen senere.'
                : err?.error?.message ||
                  'Kunne ikke hente event. Prøv igen senere.';
            this.cdr.markForCheck();
          });
        },
      });
  }

  getEventTitle(): string {
    if (!this.event?.description?.short_description?.['da']?.translation_text) {
      return 'Unavngivet begivenhed';
    }
    return this.event.description.short_description['da'].translation_text;
  }

  getEventDescription(): string {
    if (!this.event?.description?.long_description?.['da']?.translation_text) {
      return '';
    }
    return this.event.description.long_description['da'].translation_text;
  }

  getEventLocation(): string {
    if (this.event?.predefined_location?.locations_name) {
      return this.event.predefined_location.locations_name;
    }
    if (this.event?.manual_location) {
      return this.event.manual_location;
    }
    if (this.event?.manual_location_address) {
      return this.event.manual_location_address;
    }
    return '';
  }

  /** Escape and fold a single ICS content line (max 75 octets per line, fold with CRLF + space). */
  private icsEscape(s: string): string {
    if (!s) return '';
    const escaped = String(s)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
    const lines: string[] = [];
    let remaining = escaped;
    const maxLen = 75;
    while (remaining.length > maxLen) {
      lines.push(remaining.slice(0, maxLen));
      remaining = ' ' + remaining.slice(maxLen);
    }
    if (remaining.length > 0) {
      lines.push(remaining);
    }
    return lines.join('\r\n');
  }

  /** Format ISO date string to ICS UTC format YYYYMMDDTHHmmssZ */
  private toIcsUtc(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const h = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    const sec = String(d.getUTCSeconds()).padStart(2, '0');
    return `${y}${m}${day}T${h}${min}${sec}Z`;
  }

  buildIcsContent(): string {
    if (!this.event) return '';
    const title = this.getEventTitle();
    const description = this.getEventDescription();
    const location = this.getEventLocation();
    const startIcs = this.toIcsUtc(this.event.start_date);
    const endIcs = this.toIcsUtc(this.event.end_date);
    const uid = this.event.guid
      ? `${this.event.guid}@duna`
      : `event-${this.event.event_id}@duna`;
    const now = new Date();
    const dtstamp = this.toIcsUtc(now.toISOString());

    const lines: string[] = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//DUNA//Event ICS//DA',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${startIcs}`,
      `DTEND:${endIcs}`,
      `SUMMARY:${this.icsEscape(title)}`,
    ];
    if (description) {
      lines.push(`DESCRIPTION:${this.icsEscape(description)}`);
    }
    if (location) {
      lines.push(`LOCATION:${this.icsEscape(location)}`);
    }
    lines.push('END:VEVENT', 'END:VCALENDAR');
    return lines.join('\r\n');
  }

  downloadIcs(): void {
    if (!this.event) return;
    const ics = this.buildIcsContent();
    const blob = new Blob([ics], { type: 'text/calendar; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-${this.event.guid || this.event.event_id}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
